import { UserForm } from '@/features/UserForm'
import { BasePage } from '../BasePage'
import { MainTitle } from '@/components/title/MainTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useFetch } from '@/hooks/useFetch'
import { ChangeEvent, useEffect, useState } from 'react'
import { initialUserFormData } from '@/constants/initialUserFormData'
import { UsersFormData } from '@/types/usersFormData'
import { showError, showSuccess } from '@/shared/toast'
import { SingleValue } from 'react-select'
import { useSaveUser } from '@/hooks/features/user/useSaveUser'
import { useUserValidation } from '@/hooks/features/user/useUserValidation'
import { isAxiosError } from 'axios'
import { useAtom, useSetAtom } from 'jotai'
import { isDisabledAtom } from '@/store/state'
import { trimFull } from '@/utils/inputFormatter'
import { CancelFormConfirmModal } from '@/features/CancelFormConfirmModal/Component'

export const UserFormPage = () => {
  const id = useParams().id
  const title = id ? 'アカウント更新' : 'アカウント登録'
  const navigate = useNavigate()
  const { save } = useSaveUser()
  const { validate } = useUserValidation()
  const [formData, setFormData] = useState<UsersFormData>(initialUserFormData)
  const [initialFormData, setInitialFormData] =
    useState<UsersFormData>(initialUserFormData)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false)
  const normalizeFormData = (data: UsersFormData): UsersFormData => {
    return Object.fromEntries(
      Object.entries(initialUserFormData).map(([key, initialValue]) => [
        key,
        data[key as keyof UsersFormData] ?? initialValue,
      ]),
    ) as UsersFormData
  }
  const { data, error }: { data: UsersFormData | null; error: Error | null } =
    useFetch(id ? `/api/users/${id}` : null)

  const setIsDisabled = useSetAtom(isDisabledAtom)

  useEffect(() => {
    setIsDisabled(false)
    if (id && data) {
      const nextFormData = normalizeFormData(data)
      setFormData(nextFormData)
      setInitialFormData(nextFormData)
    }
  }, [id, data])

  useEffect(() => {
    if (error) {
      showError('アカウント情報の取得に失敗しました。')
    }
  }, [error])

  const trimFormData = (formData: UsersFormData): UsersFormData => {
    return {
      ...formData,
      name: trimFull(formData.name),
    }
  }
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    setIsDisabled(true)

    const trimmedFormData = trimFormData(formData)

    const errors = validate(trimmedFormData, id)
    if (Object.keys(errors).length > 0) {
      setErrors(errors)
      setIsDisabled(false)
      return
    }
    save(trimmedFormData, id)
      .then(() => {
        showSuccess(`アカウント情報を${id ? '更新' : '登録'}しました。`)
        navigate('/users')
      })
      .catch((err) => {
        if (isAxiosError(err) && err.response?.status === 422) {
          const responseErrors = err.response.data.errors as Record<
            string,
            string[]
          >

          // TODO Laravel側のバリデーションと合わせて見直し
          if (responseErrors?.loginId) {
            setErrors((prev) => ({
              ...prev,
              loginId: responseErrors.loginId.join('\n'),
            }))
            return
          }
        }
        showError(`アカウント情報の${id ? '更新' : '登録'}に失敗しました。`)
      })
      .finally(() => {
        setIsDisabled(false)
      })
  }
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const handleSelectChange = (
    newValue: SingleValue<{
      label: string
      value: number
    }>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      role: newValue?.value || 0,
    }))
    setErrors((prev) => ({ ...prev, role: '' }))
  }

  const handleCancel = () => {
    if (JSON.stringify(formData) !== JSON.stringify(initialFormData)) {
      setShowCancelConfirmModal(true)
      return
    }
    navigate('/users')
  }

  return (
    <>
      <BasePage title={title}>
        <MainTitle>{title}</MainTitle>
        <UserForm
          formData={formData}
          errors={errors}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isCreate={!id}
          id={id}
        />
      </BasePage>
      {showCancelConfirmModal && (
        <CancelFormConfirmModal
          onConfirm={() => navigate('/users')}
          onCancel={() => setShowCancelConfirmModal(false)}
        />
      )}
    </>
  )
}
